import React, { useState, useEffect, useCallback } from "react";
import { Crown, CheckCircle2, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import { apiRequest } from "../api";

export default function Vip() {
  const [user, setUser] = useState(null);
  const [vipLevels, setVipLevels] = useState([]);
  const [activeVips, setActiveVips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const current = vipLevels.find(function (level) {
    return Number(level.id) === Number(user && user.vip_level);
  });

  const loadData = useCallback(async function () {
    try {
      setError("");

      const results = await Promise.all([
        apiRequest("/users/me"),
        apiRequest("/vip-levels"),
      ]);

      const profile = results[0];
      const levels = results[1];

      setUser(profile);
      setVipLevels(Array.isArray(levels) ? levels : []);

      try {
        const active = await apiRequest("/vip-levels/my-active");

        if (Array.isArray(active)) {
          setActiveVips(active);
        } else {
          setActiveVips([]);
        }
      } catch (activeError) {
        console.warn("Active VIP endpoint unavailable:", activeError);

        const fallbackVip = (Array.isArray(levels) ? levels : [])
          .filter(function (level) {
            return Number(level.id) === Number(profile && profile.vip_level);
          })
          .map(function (level) {
            return {
              id: Number(level.id),
              name: level.name || "VIP " + String(level.id),
              deposit: Number(
                level.usdt_required !== undefined
                  ? level.usdt_required
                  : level.points_required !== undefined
                  ? level.points_required
                  : level.deposit !== undefined
                  ? level.deposit
                  : 0
              ),
              daily: Number(
                level.daily_usdt !== undefined
                  ? level.daily_usdt
                  : level.daily_points !== undefined
                  ? level.daily_points
                  : level.daily_earnings !== undefined
                  ? level.daily_earnings
                  : level.daily !== undefined
                  ? level.daily
                  : 0
              ),
              color: level.color || "#5B8DEF",
              status: "active",
            };
          });

        setActiveVips(fallbackVip);
      }
    } catch (err) {
      console.error("VIP page load error:", err);
      setError(err.message || "Failed to load VIP levels.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function () {
    loadData();
  }, [loadData]);

  const handleSelectPlan = async function (id) {
    const selectedLevel = vipLevels.find(function (level) {
      return Number(level.id) === Number(id);
    });

    if (!selectedLevel) {
      alert("VIP level not found.");
      return;
    }

    const required = Number(
      selectedLevel.usdt_required !== undefined
        ? selectedLevel.usdt_required
        : selectedLevel.points_required !== undefined
        ? selectedLevel.points_required
        : selectedLevel.deposit !== undefined
        ? selectedLevel.deposit
        : 0
    );

    const balance = Number((user && user.usdt_balance) || 0);

    if (!Number.isFinite(required) || required <= 0) {
      alert("Invalid VIP deposit amount.");
      return;
    }

    if (!Number.isFinite(balance)) {
      alert("Invalid USDT balance.");
      return;
    }

    if (balance < required) {
      alert(
        "Insufficient USDT.\n\nRequired: " +
          required.toFixed(2) +
          " USDT\nAvailable: " +
          balance.toFixed(2) +
          " USDT."
      );
      return;
    }

    const vipName = selectedLevel.name || "VIP " + String(selectedLevel.id);

    const confirmed = window.confirm(
      "Are you sure you want to activate " +
        vipName +
        "?\n\nRequired: " +
        required.toFixed(2) +
        " USDT\nAvailable: " +
        balance.toFixed(2) +
        " USDT"
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await apiRequest("/vip-levels/" + id + "/unlock", {
        method: "POST",
      });

      alert((response && response.message) || vipName + " activated successfully.");

      await loadData();
    } catch (err) {
      console.error("VIP activation error:", err);
      alert((err && err.message) || "Unable to activate VIP level.");
    }
  };

  const activeVipLevels = activeVips.length > 0 ? activeVips : current ? [current] : [];

  if (loading) {
    return (
      <Layout title="VIP Levels">
        <div className="flex items-center justify-center py-24">
          <div className="flex items-center gap-2 text-sm text-[#7C8AA5]">
            <Loader2 size={18} className="animate-spin" />
            Loading VIP levels...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="VIP Levels">
      <div className="max-w-5xl mx-auto space-y-5">
        <div>
          <div className="flex items-center gap-2">
            <Crown size={20} className="text-[#F5B942]" />
            <h1 className="text-lg font-bold text-white">VIP Levels</h1>
          </div>
          <p className="text-xs text-[#7C8AA5] mt-1">
            Choose a VIP level to activate and start earning daily.
          </p>
        </div>

        {error ? (
          <div className="rounded-xl bg-[#E5566B]/10 border border-[#E5566B]/20 text-[#E5566B] text-sm px-4 py-3">
            {error}
          </div>
        ) : null}

        {/* CURRENT VIP SUMMARY */}
        {current ? (
          <div className="rounded-2xl border border-[#8B5CF6]/30 bg-gradient-to-br from-[#2A1C52]/50 to-[#0E1220] p-5 flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#8B5CF6]/20">
              <Crown size={26} className="text-[#B79CFF]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#B7A9E0]">
                Current Plan
              </div>
              <div className="text-lg font-bold text-white">{current.name}</div>
              <div className="text-xs text-[#B7A9E0] mt-0.5">
                Earning{" "}
                {Number(
                  current.daily_usdt !== undefined
                    ? current.daily_usdt
                    : current.daily_points !== undefined
                    ? current.daily_points
                    : current.daily_earnings !== undefined
                    ? current.daily_earnings
                    : current.daily !== undefined
                    ? current.daily
                    : 0
                )}{" "}
                USDT daily
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-5 text-center">
            <div className="text-sm text-[#7C8AA5]">
              You don't have an active VIP plan yet. Select one below to start earning.
            </div>
          </div>
        )}

        {/* VIP GRID */}
        {vipLevels.length === 0 ? (
          <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-8 text-center">
            <div className="text-sm text-[#7C8AA5]">No VIP levels available.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {vipLevels.map(function (lvl) {
              const isActive =
                Number(user && user.vip_level) === Number(lvl.id) ||
                activeVips.some(function (vip) {
                  return Number(vip.id !== undefined ? vip.id : vip.vip_level_id) === Number(lvl.id);
                });

              const deposit = Number(
                lvl.usdt_required !== undefined
                  ? lvl.usdt_required
                  : lvl.points_required !== undefined
                  ? lvl.points_required
                  : lvl.deposit !== undefined
                  ? lvl.deposit
                  : 0
              );

              const daily = Number(
                lvl.daily_usdt !== undefined
                  ? lvl.daily_usdt
                  : lvl.daily_points !== undefined
                  ? lvl.daily_points
                  : lvl.daily_earnings !== undefined
                  ? lvl.daily_earnings
                  : lvl.daily !== undefined
                  ? lvl.daily
                  : 0
              );

              const color = lvl.color || "#5B8DEF";
              const name = lvl.name || "VIP " + String(lvl.id);

              return (
                <div
                  key={lvl.id}
                  className={
                    "rounded-2xl border p-4 transition-all " +
                    (isActive
                      ? "border-[#8B5CF6]/60 bg-[#8B5CF6]/10 shadow-[0_0_0_1px_rgba(139,92,246,0.3)]"
                      : "border-[#1B2033] bg-[#0E1220]")
                  }
                >
                  <div className="flex items-center justify-between mb-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-xl"
                      style={{ backgroundColor: color + "22", color: color }}
                    >
                      <Crown size={18} />
                    </div>
                    {isActive ? (
                      <CheckCircle2 size={18} className="text-[#3FBF8F]" />
                    ) : null}
                  </div>

                  <div className="text-base font-bold text-white mb-3">{name}</div>

                  <div className="text-[10px] text-[#7C8AA5] uppercase tracking-wide">
                    Deposit
                  </div>
                  <div className="text-sm font-semibold text-white mb-2">
                    {deposit} USDT
                  </div>

                  <div className="text-[10px] text-[#7C8AA5] uppercase tracking-wide">
                    Daily
                  </div>
                  <div className="text-sm font-semibold mb-4" style={{ color: color }}>
                    {daily} USDT
                  </div>

                  <button
                    onClick={function () {
                      handleSelectPlan(lvl.id);
                    }}
                    disabled={isActive}
                    className={
                      "w-full py-2.5 rounded-xl text-sm font-semibold transition " +
                      (isActive
                        ? "bg-[#3FBF8F]/15 text-[#3FBF8F] cursor-default"
                        : "text-white hover:opacity-90")
                    }
                    style={isActive ? undefined : { backgroundColor: color }}
                  >
                    {isActive ? "Active" : "Select Plan"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* ACTIVE VIPS LIST */}
        {activeVipLevels.length > 0 ? (
          <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-5 sm:p-6">
            <div className="text-[11px] uppercase tracking-widest text-[#7C8AA5] mb-3">
              Active VIP Levels
            </div>
            <div className="space-y-2">
              {activeVipLevels.map(function (level, index) {
                return (
                  <div
                    key={level.user_vip_id || level.id || index}
                    className="flex items-center justify-between bg-[#161B2C] rounded-xl px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3FBF8F]" />
                      <span className="text-sm text-white">
                        {level.name || "VIP " + String(level.id)}
                      </span>
                    </div>
                    <span className="text-xs text-[#3FBF8F] font-medium">Active</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </Layout>
  );
}